import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';

/**
 * Buyer Centre — the education-first hub.
 *
 * Editorial magazine layout: a cover statement, twelve pillar cards, one CTA
 * to the Strategy Session at the bottom. Sales second. Education first.
 */
export default function BuyerCentre() {
    const { data } = useQuery({
        queryKey: ['buyer-centre'],
        queryFn: async () => (await api.get('/buyer-centre')).data,
    });
    const items = data?.items || [];

    return (
        <div className="bg-white">
            <SEOHead
                title="Buyer Centre — Southern Ontario"
                description="The complete education hub for Southern Ontario home buyers. Twelve pillars: buying roadmap, mortgage planning, neighbourhood selection, offer strategy, closing costs and more."
                path="/buyer-centre"
            />

            {/* ==================== COVER ==================== */}
            <section className="border-b border-neutral-200 bg-[#FAFAF8]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pb-28">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        The Buyer Centre · Southern Ontario
                    </div>
                    <h1
                        className="font-editorial mt-8 leading-[1.02] text-black max-w-4xl"
                        style={{ fontSize: 'clamp(52px, 8vw, 108px)', letterSpacing: '-0.03em' }}
                        data-testid="buyer-centre-title"
                    >
                        Education first.
                        <br />
                        <span className="italic text-neutral-500">Sales second.</span>
                    </h1>
                    <p className="mt-8 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: 'clamp(17px, 1.3vw, 21px)', lineHeight: 1.55 }}>
                        Twelve pillars — the entire playbook for buying a home in Southern Ontario.
                        Written by independent research, not commissioned by a brokerage.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <Link
                            to="/buyer-centre/buying-roadmap"
                            data-testid="buyer-centre-start"
                            className="inline-flex items-center gap-2 bg-brand-navy text-white px-7 py-4 text-[14px] font-medium hover:bg-neutral-800"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            <BookOpen className="w-4 h-4" /> Start with the roadmap
                        </Link>
                        <Link
                            to="/strategy-session"
                            className="inline-flex items-center gap-2 border border-neutral-400 text-black px-7 py-4 text-[14px] font-medium hover:bg-brand-navy hover:text-white transition-colors"
                           
                        >
                            Or book a strategy session
                        </Link>
                    </div>
                </div>
            </section>

            {/* ==================== PILLAR GRID ==================== */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 border-l border-t border-neutral-200">
                    {items.map((c, i) => (
                        <motion.div
                            key={c.slug}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-40px' }}
                            transition={{ duration: 0.5, delay: (i % 4) * 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                            className="border-r border-b border-neutral-200"
                        >
                            <Link
                                to={`/buyer-centre/${c.slug}`}
                                data-testid={`buyer-pillar-${c.slug}`}
                                className="group block relative bg-white hover:bg-brand-navy hover:text-white transition-colors duration-500 h-full"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-200">
                                    <img src={c.hero_image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 60%)' }} />
                                    <div className="absolute top-3 left-3 font-mono-data text-[10px] tracking-[0.24em] uppercase text-white/85 tabular">
                                        {String(i + 1).padStart(2, '0')}
                                    </div>
                                </div>
                                <div className="p-5 lg:p-6 min-h-[180px] flex flex-col">
                                    <div className="font-editorial text-[22px] tracking-editorial leading-tight group-hover:italic">
                                        {c.name}
                                    </div>
                                    <p className="mt-3 text-[13px] leading-relaxed text-neutral-600 group-hover:text-white/80 flex-1">
                                        {c.kicker}
                                    </p>
                                    <div className="mt-4 flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-neutral-500 group-hover:text-white/70 font-mono-data">
                                        Read <ArrowUpRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ==================== BOTTOM CTA ==================== */}
            <section className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-white/60 font-mono-data flex items-center gap-3">
                            <span className="inline-block w-6 h-px bg-white/60" />
                            When you&apos;re ready
                        </div>
                        <h2 className="font-editorial mt-6 leading-[1.03]" style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}>
                            The reading only gets you halfway.
                        </h2>
                        <p className="mt-6 font-editorial text-white/80 max-w-xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                            The other half is a thirty-minute conversation with an independent analyst — your
                            shortlist, your numbers, your timeline. No listings pitched.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <Link
                            to="/strategy-session"
                            data-testid="buyer-centre-strategy-cta"
                            className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 text-[14px] font-medium hover:bg-neutral-200"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            Book a strategy session <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
